import { Container, IDestroyOptions, Point, Sprite, Texture } from "pixi.js";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { HourlyBonusSection } from "./HourlyBonusSection";
import { HourlyBonusSectionType } from "./HourlyBonusSectionType";
import { HourlyBonusSectionViewDescription } from "./HourlyBonusSectionViewDescription";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
import { nameof } from "bindable-data";
import { Event } from "event-system";
import { IFortuneWheelController } from "../../controller/IFortuneWheelController";
import { HourlyBonusItemData } from "../../../model/HourlyBonusItemData";
import { gsap } from "gsap";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { HourlyBonusPanelInfoText } from "./HourlyBonusPanelInfoText";
import { Hand } from "../../../../../common/components/Hand";
import { IAnalyticProviderWrapper } from "by/gamefactory/analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "by/gamefactory/analytics/events/VirtualScreenEvent";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { HourlyBonusDiceCollectAnimation } from "./HourlyBonusDIceCollectAnimation";
type Timeline = gsap.core.Timeline;

export class HourlyBonusPanel extends Container {
    private static readonly SECTIONS_VIEW_DESCRIPTION = new Array<HourlyBonusSectionViewDescription>(
        { position: new Point(-277, 5), type: HourlyBonusSectionType.left },
        { position: new Point(-94, 5), type: HourlyBonusSectionType.middle },
        { position: new Point(97, 5), type: HourlyBonusSectionType.middle },
        { position: new Point(279, 5), type: HourlyBonusSectionType.right }
    );
    public readonly onCompleteAllRewards: Event;
    public readonly onCollectDicesReward: Event;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly canvasScaler: ICanvasScaler;
    private background: Sprite;
    private bonusSections: Array<HourlyBonusSection>;
    private currentSectionArrow: Sprite;
    private arrowAnimation: Timeline;
    private bgAnimation: Timeline;
    private infoText: HourlyBonusPanelInfoText;
    private infoTextAnimation: Timeline;
    private handAnimation: Timeline;
    private hand: Hand;

    public constructor(
        commonResources: CommonResourcesPackage,
        wheelResources: HourlyBonusResourcePackage,
        fortuneWheelModel: IFortuneWheelModel,
        fortuneWheelController: IFortuneWheelController,
        analyticsProvider: IAnalyticProviderWrapper,
        canvasScaler: ICanvasScaler
    ) {
        super();
        this.commonResources = commonResources;
        this.wheelResources = wheelResources;
        this.fortuneWheelModel = fortuneWheelModel;
        this.fortuneWheelController = fortuneWheelController;
        this.analyticsProvider = analyticsProvider;
        this.canvasScaler = canvasScaler;
        this.bonusSections = [];
        this.onCompleteAllRewards = new Event();
        this.onCollectDicesReward = new Event();
        this.arrowAnimation = new gsap.core.Timeline();
        this.bgAnimation = new gsap.core.Timeline();
        this.infoTextAnimation = new gsap.core.Timeline();
        this.handAnimation = new gsap.core.Timeline();
        this.build();
        this.once("added", this.addedToStage.bind(this));
        this.fortuneWheelModel.propertyChanged.add(this.onFortuneWheelModelPropertyChanged, this);
    }

    private async onFortuneWheelModelPropertyChanged(model: IFortuneWheelModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "currentHourlyBonusSection"):
                const item = this.fortuneWheelModel.hourlyBonusItems.value[this.fortuneWheelModel.currentHourlyBonusSection.value];
                const arrowPosOffset = item?.type === "WHEEL" ? 7 : -10;
                this.arrowAnimation.to(this.currentSectionArrow, {
                    x: HourlyBonusPanel.SECTIONS_VIEW_DESCRIPTION[this.fortuneWheelModel.currentHourlyBonusSection.value].position.x + arrowPosOffset,
                    y: -2,
                    ease: "power1.inOut",
                    duration: 0.3
                });
                await this.arrowAnimation.play();
                this.arrowAnimation.clear();
                break;
            case nameof(model, "hourlyBonusItems"):
                if (model.spinAvailableIn.value == 0) {
                    this.showHand();
                }
                this.updateTextsAnimation();
                model.spinAvailableIn.value === 0 ?
                    this.switchBgTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_bg_png) :
                    this.switchBgTextureAnimation(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_base_bg_png);
                this.bonusSections.forEach((section, index) => { section.updateData(this.fortuneWheelModel.hourlyBonusItems.value[index]); });
                break;
        }
    }

    private build(): void {
        this.background = new Sprite(this.fortuneWheelModel.spinAvailableIn.value === 0 ? this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_bg_png : this.wheelResources.assets_views_hourly_bonus_hourly_bonus_base_bg_png);
        this.background.anchor.set(0.5);
        this.addChild(this.background);
        HourlyBonusPanel.SECTIONS_VIEW_DESCRIPTION.forEach((itemDescription, index) => {
            const itemData = this.fortuneWheelModel.hourlyBonusItems.value[index];
            const hourlyBonusSection = new HourlyBonusSection(this.wheelResources, this.fortuneWheelModel, itemDescription, itemData, this.canvasScaler);
            hourlyBonusSection.on("pointerdown", this.hourlyBonusSectionClickHandle.bind(this, itemData, hourlyBonusSection));
            hourlyBonusSection.position = itemDescription.position;
            this.addChild(hourlyBonusSection);
            this.bonusSections.push(hourlyBonusSection);
        });
        this.currentSectionArrow = new Sprite(this.wheelResources.assets_views_hourly_bonus_hourly_bonus_arrows_png);
        this.currentSectionArrow.anchor.set(0.5);
        const item = this.fortuneWheelModel.hourlyBonusItems.value[this.fortuneWheelModel.currentHourlyBonusSection.value];
        const arrowPosOffset = item?.type === "WHEEL" ? 7 : -10;
        this.currentSectionArrow.position.set(HourlyBonusPanel.SECTIONS_VIEW_DESCRIPTION[this.fortuneWheelModel.currentHourlyBonusSection.value].position.x + arrowPosOffset, -2);
        this.addChild(this.currentSectionArrow);
        this.infoText = new HourlyBonusPanelInfoText(this.fortuneWheelModel);
        this.infoText.position.y = -215;
        this.addChild(this.infoText);
        this.hand = new Hand(this.commonResources);
        this.hand.position.y = - 100;
        this.hand.scale.set(-0.7, 0.7);
        this.addChild(this.hand);
    }

    private addedToStage(): void {
        if (this.fortuneWheelModel.spinAvailableIn.value == 0) {
            this.showHand();
        } else {
            this.hand.visible = false;
        }
    }

    private async hourlyBonusSectionClickHandle(itemData: HourlyBonusItemData, hourlyBonusSection: HourlyBonusSection): Promise<void> {
        hourlyBonusSection.eventMode = "none";
        await this.hideHand();
        if (itemData?.type === "WHEEL") {
            this.infoTextAnimation.to(this.infoText.scale, { x: 0, y: 0, duration: 0.2, ease: "back.in" });
            await this.infoTextAnimation.play();
            this.infoTextAnimation.clear();
            this.onCompleteAllRewards.emit();
        } else {
            await this.fortuneWheelController.claimHourlyBonusReward();
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Players collect hourly bonus", this.analyticsProvider.currentEventIndex, undefined, itemData.hourlyBonusSection + 1));
            await this.playDiceCollectAnimation(itemData);
            this.onCollectDicesReward.emit();
            await this.fortuneWheelController.updateFortuneWheelStatus();
        }
    }

    private async playDiceCollectAnimation(itemData: HourlyBonusItemData): Promise<void> {
        const collectAnimationPoint = new Point();
        const diceCollectAnimation = new HourlyBonusDiceCollectAnimation(
            [this.commonResources.assets_common_ui_dices_dice_01_png, this.commonResources.assets_common_ui_dices_dice_03_png],
            Texture.EMPTY,
            itemData.hourlyBonusSection === 0 ? -300 : itemData.hourlyBonusSection === 1 ? -150 : 100,
            15,
            collectAnimationPoint
        );
        const startPosition = this.bonusSections[itemData.hourlyBonusSection].position;
        diceCollectAnimation.position.set(startPosition.x - 50, startPosition.y + 40);
        diceCollectAnimation.scale.set(0.5);
        collectAnimationPoint.set(-diceCollectAnimation.position.x * 1.7, this.height);
        this.addChild(diceCollectAnimation);
        await diceCollectAnimation.startAnimation();
        diceCollectAnimation.destroy();
    }

    private async switchBgTextureAnimation(texture: Texture): Promise<void> {
        if (this.background.texture == texture) {
            return;
        }
        this.bgAnimation.to(this.background, { alpha: 0.3, duration: 0.15 });
        this.bgAnimation.set(this.background, { texture: texture });
        this.bgAnimation.to(this.background, { alpha: 1, duration: 0.15 });
        await this.bgAnimation.play();
        this.bgAnimation.clear();
    }

    private async updateTextsAnimation(): Promise<void> {
        this.infoTextAnimation.to(this.infoText.scale, { x: 0, y: 0, duration: 0.2, ease: "back.in" });
        await this.infoTextAnimation.play();
        this.infoTextAnimation.clear();
        this.infoText.updateTexts();
        this.infoTextAnimation.to(this.infoText.scale, { x: 1, y: 1, duration: 0.2, ease: "back.out" });
        await this.infoTextAnimation.play();
        this.infoTextAnimation.clear();
    }

    private async showHand(): Promise<void> {
        const currentSectionPosition = HourlyBonusPanel.SECTIONS_VIEW_DESCRIPTION[this.fortuneWheelModel.currentHourlyBonusSection.value].position;
        this.hand.position.set(currentSectionPosition.x + this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_left_png.width - 30, currentSectionPosition.y - this.wheelResources.assets_views_hourly_bonus_hourly_bonus_ready_left_png.height * 0.5);
        this.handAnimation.set(this.hand, { visible: true });
        this.handAnimation.to(this.infoText, {
            x: this.fortuneWheelModel.currentHourlyBonusSection.value < 2 ?
                this.hand.position.x + this.infoText.width * 0.21 :
                this.hand.position.x - this.infoText.width * 1,
            duration: 0.3,
            ease: "power.out"
        })
        this.handAnimation.fromTo(this.hand, { y: this.height, angle: 90 }, { x: this.hand.x, y: this.hand.y - 420, angle: 0, duration: 0.6, ease: "power.out" });
        await this.handAnimation.play();
        this.handAnimation.clear();
    }

    private async hideHand(): Promise<void> {
        this.handAnimation.to(this.infoText, { x: 0, duration: 0.3, ease: "power.in" })
        this.handAnimation.to(this.hand, { y: this.height, angle: 90, duration: 0.5, ease: "power.in" }, "<");
        this.handAnimation.set(this.hand, { visible: false });
        await this.handAnimation.play();
        this.handAnimation.clear();
    }

    public killCollectAnimation(): void {
        this.bonusSections.forEach(section => {
            section.destroyCollectAnimation();
        });
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.fortuneWheelModel.propertyChanged.remove(this.onFortuneWheelModelPropertyChanged, this);
        this.bonusSections.forEach(section => section.off("touchstart"));
        this.arrowAnimation.kill();
        this.bgAnimation.kill();
        this.infoTextAnimation.kill();
        this.handAnimation.kill();
        super.destroy(options);
    }
}
